import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DropdownContext = createContext(null);

// Cache to avoid repeated network calls
const _cache = {};

export function DropdownProvider({ children }) {
  const [countries, setCountries] = useState([]);
  const [phoneCodes, setPhoneCodes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load countries + phone codes + currencies on mount
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (!_cache.countries) {
          const res = await axios.get(`${BACKEND_URL}/api/dropdown/countries?limit=300`);
          _cache.countries = res.data.items || [];
        }
        if (!_cache.phoneCodes) {
          const res = await axios.get(`${BACKEND_URL}/api/dropdown/phone-codes`);
          _cache.phoneCodes = res.data.items || [];
        }
        if (!_cache.currencies) {
          const res = await axios.get(`${BACKEND_URL}/api/dropdown/currencies`);
          _cache.currencies = res.data.items || [];
        }
        setCountries(_cache.countries);
        setPhoneCodes(_cache.phoneCodes);
        setCurrencies(_cache.currencies);
      } catch (err) {
        console.error('DropdownContext load error', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const getCities = useCallback(async (countryCode) => {
    if (!countryCode) return [];
    const key = `cities_${countryCode}`;
    if (_cache[key]) return _cache[key];
    try {
      const res = await axios.get(`${BACKEND_URL}/api/dropdown/countries/${countryCode}/cities`);
      _cache[key] = res.data.items || [];
      return _cache[key];
    } catch {
      return [];
    }
  }, []);

  const getVenues = useCallback(async (countryCode, city) => {
    if (!countryCode) return [];
    const key = `venues_${countryCode}_${city || ''}`;
    if (_cache[key]) return _cache[key];
    try {
      const params = new URLSearchParams({ country_code: countryCode });
      if (city) params.append('city', city);
      const res = await axios.get(`${BACKEND_URL}/api/dropdown/venues?${params}`);
      _cache[key] = res.data.items || [];
      return _cache[key];
    } catch {
      return [];
    }
  }, []);

  const invalidateCache = (key) => {
    if (key) delete _cache[key];
    else Object.keys(_cache).forEach(k => delete _cache[k]);
  };

  return (
    <DropdownContext.Provider value={{ countries, phoneCodes, currencies, loading, getCities, getVenues, invalidateCache }}>
      {children}
    </DropdownContext.Provider>
  );
}

export function useDropdown() {
  const ctx = useContext(DropdownContext);
  if (!ctx) throw new Error('useDropdown must be used within DropdownProvider');
  return ctx;
}

// Standalone hook for cities (fetches on countryCode change)
export function useCities(countryCode) {
  const { getCities } = useDropdown();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!countryCode) { setCities([]); return; }
    let cancelled = false;
    setLoading(true);
    getCities(countryCode).then(c => {
      if (!cancelled) setCities(c);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [countryCode, getCities]);

  return { cities, loading };
}

// Standalone hook for venues
export function useVenues(countryCode, city) {
  const { getVenues } = useDropdown();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!countryCode) { setVenues([]); return; }
    let cancelled = false;
    setLoading(true);
    getVenues(countryCode, city).then(v => {
      if (!cancelled) setVenues(v);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [countryCode, city, getVenues]);

  return { venues, loading };
}

// Phone field component — code select + number input
export function PhoneField({ value = { code: '+90', number: '' }, onChange, label = 'Phone', required = false, className = '' }) {
  const { phoneCodes } = useDropdown();

  const handleCode = (e) => onChange({ ...value, code: e.target.value });
  const handleNumber = (e) => {
    const raw = e.target.value.replace(/[^\d]/g, '').slice(0, 12);
    onChange({ ...value, number: raw });
  };

  return (
    <div className={className}>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}{required && <span className="text-orange-500 ml-0.5">*</span>}</label>}
      <div className="flex gap-2">
        <select
          value={value.code}
          onChange={handleCode}
          className="w-36 border border-gray-200 rounded-lg px-2 py-2.5 text-sm focus:ring-2 focus:ring-orange-100 focus:border-orange-400 outline-none bg-white"
        >
          {phoneCodes.map(c => (
            <option key={c.code} value={c.phone_code}>{c.flag} {c.phone_code}</option>
          ))}
        </select>
        <input
          type="text"
          inputMode="numeric"
          value={value.number}
          onChange={handleNumber}
          placeholder="5001234567"
          maxLength={12}
          className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-orange-100 focus:border-orange-400 outline-none"
        />
      </div>
    </div>
  );
}
